import { ReactNode } from "react";
import { Button } from "antd";
import dayjs from "dayjs";
import { StatusWithIcon, UserBubble } from "@shared/ui";
import { fallbackString } from "@shared/utils";
import { useUserInfo } from "./hooks/use-user-info";
import "./journal-record-info.scss";

interface RecordUser {
  userId?: string;
  firstName?: string;
  surname?: string;
  middleName?: string;
}

interface JournalRecordInfoProps {
  updateDate?: string;
  updateUserId?: string;
  status?: string;
  statusUpdateDate?: string;
  signerInfo?: RecordUser;
  currentVersionId?: string;
  currentVersionNumber?: number;
  versionControl?: ReactNode;
  onOpenAllVersions?: () => void;
  showVersionColumn?: boolean;
}

export const JournalRecordInfo = ({
  updateDate,
  updateUserId,
  status,
  statusUpdateDate,
  signerInfo,
  currentVersionId,
  currentVersionNumber,
  versionControl,
  onOpenAllVersions,
  showVersionColumn = true,
}: JournalRecordInfoProps) => {
  const { userInfo: updateUserInfo } = useUserInfo(updateUserId);


  const updateUserFio = updateUserInfo
    ? `${updateUserInfo.userSurname ?? ""} ${updateUserInfo.userFirstName?.charAt(0) ?? ""}. ${updateUserInfo.userMiddleName?.charAt(0) ?? ""}.`.trim()
    : "Пользователь";

  const statusUpdateUserFio = signerInfo
    ? `${signerInfo.surname ?? ""} ${signerInfo.firstName?.charAt(0) ?? ""}. ${signerInfo.middleName?.charAt(0) ?? ""}.`.trim()
    : "Пользователь";

  const renderVersionColumn = () => {
    if (!showVersionColumn) {
      return null;
    }

    if (versionControl) {
      return (
        <div className="journal-record-info__block journal-record-info__block--version">
          <div className="journal-record-info__version-header">
            <div className="journal-record-info__title journal-record-info__title--inline">
              Версии записи
            </div>
            {onOpenAllVersions ? (
              <Button
                type="link"
                className="journal-record-info__link"
                onClick={onOpenAllVersions}
              >
                Все версии
              </Button>
            ) : null}
          </div>
          <div className="journal-record-info__version-control">
            {versionControl}
          </div>
          <>
            <div className="journal-record-info__status-display">
              №{currentVersionNumber ?? "—"}
            </div>
            <div>
              <UserBubble userId={signerInfo?.userId}>
                <Button type="link" className="journal-record-info__user-link">
                  {fallbackString(statusUpdateUserFio)}
                </Button>
              </UserBubble>
            </div>
            <div className="journal-record-info__date">
              {statusUpdateDate
                ? dayjs(statusUpdateDate).format("DD.MM.YYYY")
                : "-"}{" "}
              {statusUpdateDate
                ? dayjs(statusUpdateDate).format("HH:mm")
                : ""}{" "}
              МСК
            </div>
          </>
        </div>
      );
    }

    if (!currentVersionId) {
      return (
        <div className="journal-record-info__block journal-record-info__block--version">
          <div className="journal-record-info__version-header">
            <div className="journal-record-info__title journal-record-info__title--inline">
              Версия
            </div>
            {onOpenAllVersions ? (
              <Button
                type="link"
                className="journal-record-info__link"
                onClick={onOpenAllVersions}
              >
                Все версии
              </Button>
            ) : null}
          </div>
          <div className="journal-record-info__no-versions">
            У этой записи пока нет версий
          </div>
        </div>
      );
    }

    return (
      <div className="journal-record-info__block journal-record-info__block--version">
        <div className="journal-record-info__version-header">
          <div className="journal-record-info__title journal-record-info__title--inline">
            Версии
          </div>
          {onOpenAllVersions ? (
            <Button
              type="link"
              className="journal-record-info__link"
              onClick={onOpenAllVersions}
            >
              Все версии
            </Button>
          ) : null}
        </div>
        <div className="journal-record-info__status-display">
          №{currentVersionNumber}
        </div>
        <div>
          <UserBubble userId={signerInfo?.userId}>
            <Button type="link" className="journal-record-info__user-link">
              {fallbackString(statusUpdateUserFio)}
            </Button>
          </UserBubble>
        </div>

        <div className="journal-record-info__date">
          {statusUpdateDate
            ? dayjs(statusUpdateDate).format("DD.MM.YYYY")
            : "-"}{" "}
          {statusUpdateDate ? dayjs(statusUpdateDate).format("HH:mm") : ""} МСК
        </div>
      </div>
    );
  };

  return (
    <div className="journal-record-info">
      <div className="journal-record-info__block journal-record-info__block--actions">
        <div className="journal-record-info__title">Действия</div>

        {updateDate && (
          <div className="journal-record-info__item">
            <span className="journal-record-info__label">Изменено </span>
            <UserBubble userId={updateUserId}>
              <Button type="link" className="journal-record-info__user-link">
                {fallbackString(updateUserFio)}
              </Button>
            </UserBubble>
            <span className="journal-record-info__label">
              {" "}
              {dayjs(updateDate).format("DD.MM.YYYY")}{" "}
              {dayjs(updateDate).format("HH:mm")} МСК
            </span>
          </div>
        )}
      </div>

      <div className="journal-record-info__block journal-record-info__block--statuses">
        <div className="journal-record-info__header">
          <div className="journal-record-info__title">Статусы</div>
          {/* <Button type="link" className="journal-record-info__link">
            Смотреть
          </Button> */}
        </div>

        {status && (
          <div>
            {status === "NEW" ? (
              <div className="journal-record-info__no-status">
                У данной записи пока нет статусов
              </div>
            ) : (
              <>
                <div className="journal-record-info__status-display">
                  <StatusWithIcon status={status} />
                </div>
                <UserBubble userId={signerInfo?.userId}>
                  <Button
                    type="link"
                    className="journal-record-info__user-link"
                  >
                    {fallbackString(statusUpdateUserFio)}
                  </Button>
                </UserBubble>
                <div className="journal-record-info__date">
                  {statusUpdateDate
                    ? dayjs(statusUpdateDate).format("DD.MM.YYYY")
                    : "-"}{" "}
                  {statusUpdateDate
                    ? dayjs(statusUpdateDate).format("HH:mm")
                    : ""}{" "}
                  МСК
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {renderVersionColumn()}
    </div>
  );
};
